import React, { useEffect, useState } from 'react'
import moment from 'moment'
import Papa from 'papaparse'
import * as XLSX from 'xlsx'


interface IClientMessages {
  text: string
  color?: string
  time?: string;
  stage?: string;
  considerDarkMode?: boolean;
  files?: { name: string; url: string }[] | null
}

const ClientMessages = ({
  text,
  color,
  time,
  stage,
  files,
  considerDarkMode
}: IClientMessages) => {
  const [previews, setPreviews] = useState<
    { name: string; url: string; type: string; preview?: any[][] }[]
  >([])

  useEffect(() => {
    if (!files || files.length === 0) return

    const loadPreviews = async () => {
      const loaded = await Promise.all(
        files.map(async (file) => {
          const fileName = file?.url?.split('/').pop() || ''
          const fileNameLower = fileName.toLowerCase()

          const isCsv = fileNameLower.endsWith('.csv')
          const isExcel = fileNameLower.endsWith('.xls') || fileNameLower.endsWith('.xlsx')
          const isPdf = fileNameLower.endsWith('.pdf')
          let preview: any[][] | undefined

          try {
            const res = await fetch(file.url)

            if (isCsv) {
              const text = await res.text()
              preview = Papa.parse(text, { preview: 5 }).data as any[][]
            } else if (isExcel) {
              const buffer = await res.arrayBuffer()
              const wb = XLSX.read(buffer, { type: 'array' })
              const sheet = wb.Sheets[wb.SheetNames[0]]
              preview = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][]
            }
          } catch (err) {
            console.error('Preview fetch failed:', err)
          }

          return {
            name: file.name,
            url: file.url,
            type: isCsv ? 'csv' : isExcel ? 'excel' : isPdf ? 'pdf' : 'other',
            preview,
          }
        })
      )

      setPreviews(loaded)
    }

    loadPreviews()
  }, [files])

  return (
    <div className='flex justify-end'>
      <div
        className={`!rounded-xl m-2 py-2 px-3 ${color || 'bg-bgColor-chat'} text-white ${considerDarkMode ? 'dark:bg-bgColor-darkMode' : ''} max-w-[85%]`}
      >
        <p className={`${considerDarkMode ? 'dark:text-primary-dark' : ''}`}>{text}</p>

        {(previews?.length > 0 && stage !== 'upload_template_files') && (
          <div className='flex flex-wrap gap-2 my-2'>
            {previews?.map((file, idx) => {
              const isImage = file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i)

              return (
                <a
                  key={idx}
                  href={file.url}
                  download={file.name}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='relative border border-gray-300 rounded p-2 w-fit max-w-[300px] bg-white hover:shadow-lg transition-all'
                >
                  {isImage ? (
                    <img
                      src={file.url}
                      alt={file.name}
                      className='w-24 h-24 object-cover rounded'
                    />
                  ) : file.type === 'pdf' ? (
                    <iframe
                      src={file.url}
                      title={file.name}
                      className='w-48 h-32 border rounded'
                    />
                  ) : file.preview ? (
                    <div className='overflow-x-auto max-h-32 mb-2 border'>
                      <table className='text-xs text-left border-collapse w-full'>
                        <tbody>
                          {file.preview.map((row, rowIdx) => (
                            <tr key={rowIdx}>
                              {row.map((cell, colIdx) => (
                                <td
                                  key={colIdx}
                                  className='border px-1 py-0.5 whitespace-nowrap text-black'
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className='text-sm text-gray-600'>📎 {file.name}</div>
                  )}

                  <div className='bg-gray-100 text-center text-xs rounded p-1 mt-1 truncate max-w-[250px] text-black'>
                    {file.name}
                  </div>
                </a>
              )
            })}
          </div>
        )}

        {time && (
          <p className={`text-sm text-right text-[#A4A7AE] ${considerDarkMode ? 'dark:text-gray-300' : ''}`}>
            {moment(time).format('hh:mm A')}
          </p>
        )}
      </div>
    </div>
  )
}

export default ClientMessages
